<template>
  <div>
    <div class="flex justify-content-between flex-wrap">
      <div class="flex justify-content-center">
        <label
          class="block my-3"
          data-test-id="dropdown-label"
        >
          {{ t(configs.label, null) }}
        </label>
      </div>
    </div>
    <BaseDropdown
      :model-value="selectedValue"
      :options="options"
      option-group-label="label"
      option-group-children="items"
      :disabled="isDropdownDisabled"
      @update:model-value="handleSelected"
    >
      <template #optiongroup="slotProps">
        <div class="flex align-items-center justify-content-between w-full">
          <div class="flex align-items-center">
            <i
              v-if="slotProps.option.customLabel"
              class="fa-solid fa-puzzle-piece custom-icon mr-2"
            />
            <span>{{ slotProps.option.label }}</span>
          </div>
          <span
            v-if="slotProps.option.customLabel"
            class="custom-badge"
          >CUSTOM</span>
        </div>
      </template>
    </BaseDropdown>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeMount, PropType, ref, Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { FieldConfig, ParserFieldObject, Property } from '@/types';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import getNativeContactProperties from '@/configs/native-contact-property';
import { getCustomContactPropertiesWithoutNested } from '@/configs/custom-contact-properties';
import { getUsedPropertiesInFunnel } from '@/composables/template-editor/FormActionsEvaluator';

export default defineComponent({
  name: 'FormFieldContactProperty',

  components: {
    BaseDropdown,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties', 'on-custom-property-selected', 'on-set-default-label'],

  setup(props, context) {
    const { t } = useI18n();
    const property: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties[0]);
    const selectedValue: Ref<string> = ref(property.value.value ?? props.configs.options.defaultValue);
    const typeWidget = ref(props.configs.options.typeWidget);
    const widgetWithoutCustomContactProperty = ref(['inputEmail', 'inputPhone']);

    // Check if dropdown should be disabled for specific widget types
    const isDropdownDisabled = computed(() => typeWidget.value === 'inputEmail' || typeWidget.value === 'inputPhone');

    // Auto-select property based on widget type
    const getAutoSelectedProperty = (widgetType: string): string | null => {
      const autoSelectMapping: Record<string, string> = {
        inputEmail: 'email',
        inputPhone: 'phone_number',
      };
      return autoSelectMapping[widgetType] || null;
    };

    // Mapping between widget types and allowed field types
    const getAllowedTypesForWidget = (widgetTypes: string): string[] => {
      const typeMapping: Record<string, string[]> = {
        inputText: ['text', 'number', 'decimal'],
        inputDate: ['date', 'datetime'],
        inputCheckbox: ['bool', 'list', 'text', 'number', 'decimal'],
        inputRadio: ['text', 'list', 'number', 'decimal'],
        inputSelect: ['text', 'list', 'number', 'decimal'],
        textarea: ['longtext'],
      };

      if (!widgetTypes || widgetTypes.length === 0) {
        return [];
      }

      // Get all allowed types for the widget type
      const allowedTypesSet = new Set<string>();
      const types = typeMapping[widgetTypes] || [];
      types.forEach((type) => allowedTypesSet.add(type));

      return Array.from(allowedTypesSet);
    };

    const allowedTypes = ref(getAllowedTypesForWidget(typeWidget.value));
    const options = ref<any[]>([]);
    const fullCustomSchemas = ref<any[]>([]);
    const nativeContactProperties = ref(getNativeContactProperties(t, '').map((item) => item.value));

    // Get label for a property (native or custom)
    const getPropertyLabel = (propertyValue: string): string => {
      // Check if it's a native property
      const nativeProps = getNativeContactProperties(t, '');
      const nativeProp = nativeProps.find((item) => item.value === propertyValue);

      if (nativeProp) {
        return nativeProp.fullLabel || nativeProp.label;
      }

      // Check if it's a custom property
      if (fullCustomSchemas.value.length > 0) {
        const customProp = fullCustomSchemas.value
          .flatMap((schema) => schema.properties)
          .find((prop) => prop.value === propertyValue);

        if (customProp && customProp.label) {
          return customProp.label || customProp.name;
        }
      }

      // Default fallback
      return 'Libellé';
    };

    const handleSelected = async (value: string) => {
      selectedValue.value = value;
      if (property.value) {
        property.value.value = selectedValue.value;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });

        // Get and emit the default label for the selected property
        const defaultLabel = getPropertyLabel(value);

        if (defaultLabel) {
          context.emit('on-set-default-label', {
            parentFieldId: 'label',
            defaultLabel,
          });

          // Also emit for placeholder field
          context.emit('on-set-default-label', {
            parentFieldId: 'placeholder',
            defaultLabel,
          });
        }

        // Check if selected property is a custom property (not native)
        const isNativeProperty = nativeContactProperties.value.find((item) => item.toLowerCase() === value.toLowerCase());

        if (!isNativeProperty && value && fullCustomSchemas.value.length > 0) {
          // Find the selected custom property in all schemas
          const selectedCustomProperty = fullCustomSchemas.value
            .flatMap((schema) => schema.properties)
            .find((prop) => prop.value === value);

          // Emit event for FormFieldOptions to handle
          if (selectedCustomProperty) {
            context.emit('on-custom-property-selected', {
              propertyValue: value,
              propertyType: selectedCustomProperty.type,
              hasRelationship: selectedCustomProperty.hasRelationship || false,
              options: selectedCustomProperty.options || [],
            });
          }
        }
      }
    };

    onBeforeMount(async () => {
      try {
        // Get used properties in funnel to disable them
        const usedProperties = getUsedPropertiesInFunnel();

        // Start with native properties and mark used ones as disabled
        const nativeProperties = getNativeContactProperties(t, typeWidget.value || '').map((item) => ({
          ...item,
          disabled: usedProperties.has(item.value) && item.value !== selectedValue.value,
        }));

        const baseOptions = [
          {
            label: t('templateBuilder.formContactProperty.nativeGroup'),
            customLabel: false,
            items: nativeProperties,
          },
        ];

        // Check if widget type allows custom contact properties
        const shouldLoadCustomProperties = !widgetWithoutCustomContactProperty.value.includes(typeWidget.value);

        if (shouldLoadCustomProperties) {
          // Get custom contact properties
          const customSchemas = await getCustomContactPropertiesWithoutNested();

          // Store full schemas for later analysis
          fullCustomSchemas.value = customSchemas;

          // Process each schema and filter properties by allowed types
          // Also mark used properties as disabled
          const customGroups = customSchemas
            .map((schema) => ({
              label: schema.schemaName,
              customLabel: true,
              items: schema.properties
                .filter((prop) => allowedTypes.value.includes(prop.type))
                .map((prop) => {
                  const isUsed = usedProperties.has(prop.value);
                  const isCurrentValue = prop.value === selectedValue.value;
                  const shouldDisable = isUsed && !isCurrentValue;

                  return {
                    label: prop.label || prop.name,
                    value: prop.value,
                    disabled: shouldDisable,
                  };
                }),
            }))
            .filter((group) => group.items.length > 0); // Only include groups with valid properties

          options.value = [
            ...baseOptions,
            ...customGroups,
          ];
        } else {
          // Only use native properties for widgets that don't support custom properties
          options.value = baseOptions;
        }

        // Auto-select property for specific widget types after options are loaded
        const autoSelectedProperty = getAutoSelectedProperty(typeWidget.value);
        if (autoSelectedProperty && options.value.length > 0) {
          // Find the property in the native group
          const nativeGroup = options.value.find((group) => !group.customLabel);
          if (nativeGroup) {
            const foundProperty = nativeGroup.items.find((item: { value: string }) => item.value === autoSelectedProperty);
            if (foundProperty && selectedValue.value !== autoSelectedProperty) {
              selectedValue.value = autoSelectedProperty;
              if (property.value) {
                property.value.value = autoSelectedProperty;
                context.emit('on-change-properties', {
                  selector: props.parserValues.selector,
                  properties: [property.value],
                });
              }
            }
          }
        }
      } catch (error) {
        // In case of error, still mark used properties as disabled
        const usedProperties = getUsedPropertiesInFunnel();
        const nativeProperties = getNativeContactProperties(t, typeWidget.value || '').map((item) => ({
          ...item,
          disabled: usedProperties.has(item.value) && item.value !== selectedValue.value,
        }));

        options.value = [
          {
            label: t('templateBuilder.formContactProperty.nativeGroup'),
            customLabel: false,
            items: nativeProperties,
          },
        ];

        // Auto-select property for specific widget types even in error case
        const autoSelectedProperty = getAutoSelectedProperty(typeWidget.value);
        if (autoSelectedProperty && options.value.length > 0) {
          const nativeGroup = options.value[0];
          const foundProperty = nativeGroup.items.find((item: { value: string }) => item.value === autoSelectedProperty);
          if (foundProperty && selectedValue.value !== autoSelectedProperty) {
            selectedValue.value = autoSelectedProperty;
            if (property.value) {
              property.value.value = autoSelectedProperty;
              context.emit('on-change-properties', {
                selector: props.parserValues.selector,
                properties: [property.value],
              });
            }
          }
        }
      }
    });

    return {
      t,
      selectedValue,
      options,
      handleSelected,
      isDropdownDisabled,
    };
  },

});
</script>

<style scoped>
.custom-icon {
  color: #8b5cf6;
  font-size: 0.875rem;
}

.custom-badge {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-left: 0.5rem;
}
</style>
